export interface ChatMailDraftParams {
  applicantEmail: string;
  applicantName: string;
  // Added by Amisha on 2026-09-07.
  // Reason: identify the relevant application in the staff chat email body.
  applicationNumber: string;
  departmentName: string;
  applicationType: string;
  siteAddress: string;
}

export interface ChatMailDraft {
  to: string;
  subject: string;
  body: string;
}

// Added by Amisha on 2026-07-02.
// Reason: keep the Outlook mail draft body generation isolated from the chat component UI logic.
function getMailDepartmentName(params: ChatMailDraftParams): string {
  const departmentName = params.departmentName.trim();

  // Added by Amisha on 2026-09-03.
  // Reason: Janus stores short labels such as "Building", while the mail must display "Building Department".
  if (!departmentName || /\bdepartment$/i.test(departmentName)) {
    return departmentName;
  }

  return `${departmentName} Department`;
}

// Added by Amisha on 2026-07-02.
// Reason: staff chat mail uses a fixed Outlook-friendly template while swapping in the live applicant and application values.
export function buildChatMailDraft(params: ChatMailDraftParams): ChatMailDraft {
  const departmentName = getMailDepartmentName(params);
  const siteAddress = params.siteAddress.trim() || '--';
  // Added by Amisha on 2026-09-03.
  // Reason: replace the fixed Notice of Intent subject with the selected application's type.
  const applicationType = params.applicationType.trim();
  const applicationNumber = params.applicationNumber.trim() || '--';
  const applicantName = params.applicantName.trim() || 'Applicant';
  const departmentReference = departmentName || 'the department';
  const subjectPrefix = applicationType ? `${applicationType};` : '';

  return {
    to: params.applicantEmail.trim(),
    subject: [subjectPrefix, siteAddress, departmentName]
      .filter(Boolean)
      .join(' '),
    // Updated by Amisha on 2026-09-07.
    // Reason: match the approved applicant notification wording and application-details layout.
    body: [
      `Hi ${applicantName},`,
      '',
      `You have received a new message from ${departmentReference} regarding your application.`,
      '',
      // Updated by Amisha on 2026-09-07.
      // Reason: emphasize the plain-text application details heading and separate it from the field list.
      'APPLICATION DETAILS:',
      `Application #: ${applicationNumber}`,
      `Application Type: ${applicationType || '--'}`,
      `Address: ${siteAddress}`,
      '',
      'Please log in to your account, go to My Applications, locate the application using the details above, and click the chat icon to view this message and previous messages related to the application.',
      '',
      'Regards,',
      departmentName || 'Department'
    ].join('\r\n')
  };
}
